"use client";

import { useEffect, useRef } from "react";

import { isJwtTokenLogin, getRedirectFromPortalLoginToken, isSessionDataLogin } from "@/lib/utils/auth";
import { useLoginActions } from "./useLoginActions";
import { BootAuthResponse } from "@/lib/types";
import { collectQueryParams, getUrlParam } from "@/lib/utils/url";
import { URL_PARAMS } from "@/lib/consts/params";
import { isAppleLogin, isFacebookLogin, isOTPLogin, sendLoginCanceledWebhookEvents } from "@/lib/utils/login";
import { sessionManager } from "@/lib/session";
import { AuthMethods } from "@/lib/consts/auth";
import { useGlobalLoader } from "@/providers/LoadingProvider";
import { webStorageUtil } from "@/lib/storage/webStorageUtil";
import { ELocalStorageKeys, ESessionStorageKeys } from "@/lib/storage/enums";
import { LOGIN_QUERY_PARAMS } from "@/lib/consts/params";
import { SESSION_STORAGE_KEYS } from "@/lib/consts/sessionStorage";
import { clearClientSession, clearSessionCookie, revokeExistingSession } from "@/lib/auth/revokeSession";
import {
    clearOtpLoginParamsFromUrl,
    isOtpLoginAttempted,
    markOtpLoginAttempted,
} from "@/lib/auth/otpLoginGuard";
import {
    clearPortalRedirectLoginParamsFromUrl,
    isPortalRedirectLoginAttempted,
    markPortalRedirectLoginAttempted,
} from "@/lib/auth/portalRedirectLoginGuard";
import { clearPendingOtpAccessToken, resolveOtpCredentials } from "@/lib/auth/otpHandoff";
import { featureFlagService } from "@/lib/services/featureFlag";
import { FeatureFlags } from "@/lib/consts/featureFlags";
import { isSessionTokenAlive, probeSessionToken } from "@/lib/auth/validateSession";
import { setSessionCookieWithConfirmation } from "@/lib/auth/sessionRestore";
import { navigateToShopHard } from "@/lib/utils/navigation";
import sessionService from "@/lib/services/session";
import coralogixRumService from "@/lib/services/coralogixRum";
import { LoggerEventNames } from "@/lib/consts/logger";
import { readHandoffTokenIdentity } from "@/lib/utils/handoffTokenIdentity";
import { LoginDeadEndReason, reportLoginDeadEnd } from "@/lib/analytics/loginDeadEnd";
import { captureStoreJourney } from "@/lib/analytics/storeJourney";

const markSessionRejected = (): void => {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.SESSION_REJECTED, '1');
    } catch {
        // private mode / quota — middleware cookie clear still breaks the loop
    }
};

const clearSessionRejectedMark = (): void => {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SESSION_REJECTED);
    } catch {
        // ignore
    }
};

const isSessionRejected = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_REJECTED) === '1';
    } catch {
        return false;
    }
};

// An auth callback is a request to sign in as a specific — possibly different —
// player, so it outranks the stale-session recovery below. Recovering the
// previous player's session instead would leave the device signed in as them
// and silently drop the new login. Mirrors `isLoginAuthCallback`, which is what
// lets these reach /login at the edge while a session cookie is still present.
const isExplicitAuthRequest = (): boolean =>
    Boolean(getRedirectFromPortalLoginToken()) ||
    Boolean(isJwtTokenLogin()) ||
    isOTPLogin() ||
    isAppleLogin() ||
    isFacebookLogin();

// A `session_expired` bounce is usually NOT a dead session: the HttpOnly
// cookie simply expired (or was never set on this device) while the
// localStorage token is still valid on the gateway. Purging in that case
// logs a live player out. Instead, validate the stored token against
// keepalive and, when alive, re-issue the cookie and go straight back to
// shop — mirroring what a fresh login does. Navigation happens only after a
// read-back confirms the browser stored the cookie, so a cookie-blocking
// browser deterministically falls through to the old purge/logout path
// instead of looping login <-> shop.
const tryRestoreCookieFromStoredToken = async (): Promise<boolean> => {
    if (isSessionRejected()) {
        return false;
    }
    const sessionToken =
        sessionService.getSessionToken() ?? webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
    if (!sessionToken) {
        return false;
    }
    if (!(await isSessionTokenAlive(sessionToken))) {
        return false;
    }
    if (!(await setSessionCookieWithConfirmation(sessionToken))) {
        return false;
    }
    navigateToShopHard();
    return true;
};

// Purge a token the backend already rejected (shop boot/offers returned
// 401/403). Without this the login page would auto-redirect back to /shop on
// the still-present token, bouncing shop <-> login forever. Mirrors the
// client-side 401 handler in shop/utils/api.ts: clears the in-memory + local
// token and drops the HttpOnly cookie via the clear-session route.
const purgeRejectedSession = async (): Promise<void> => {
    clearClientSession();
    markSessionRejected();
    await clearSessionCookie();
    if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete(LOGIN_QUERY_PARAMS.SESSION_EXPIRED);
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
};

export const useLoginRedirection = (authBoot: BootAuthResponse) => {
    // Render-time params are used only for useLoginActions (auto_collect etc.).
    // The session_expired check MUST read window inside the effect — an SSR
    // empty closure would skip purge and call handleSessionDataLogin → /shop.
    const queryParams = collectQueryParams(typeof window !== 'undefined' ? window.location.search : '');
    const autoLoginAttemptedRef = useRef(false);

    const localAddressFromUrl = queryParams.get(LOGIN_QUERY_PARAMS.LOCAL_ADDRESS);
    useEffect(() => {
        if (localAddressFromUrl) {
            webStorageUtil.set(ELocalStorageKeys.LOCAL_ADDRESS, localAddressFromUrl);
        }
    }, [localAddressFromUrl]);

    const {
        handlePortalLoginRedirect,
        handleJwtTokenLogin,
        handleSessionDataLogin,
        handleFacebookLogin,
        handleAppleLogin,
        handleOTPLogin,
    } = useLoginActions(authBoot, queryParams);
    const { showLoader, hideLoader } = useGlobalLoader();

    useEffect(() => {
        let cancelled = false;

        const handleLoginByQueryParams = async () => {
            const liveParams = collectQueryParams(window.location.search);
            captureStoreJourney(liveParams);
            const landedHandoffToken = getRedirectFromPortalLoginToken();

            // Name the player before anything can fail. The portal signs the id
            // into the handoff token, so unlike every other entry point a
            // pre-auth landing can be attributed without waiting for a login —
            // which is what makes an abandoned or rejected landing searchable
            // instead of anonymous.
            if (landedHandoffToken) {
                const handoffIdentity = readHandoffTokenIdentity(landedHandoffToken);
                if (handoffIdentity) {
                    coralogixRumService.setHandoffPlayerContext(handoffIdentity);
                }
            }

            // Proves the browser actually reached us and with what kind of
            // credentials. Types only — never the token values.
            coralogixRumService.sendInfoEvent(LoggerEventNames.LOGIN_LANDED, {
                hasPortalRedirectToken: Boolean(landedHandoffToken),
                isOtpCallback: isOTPLogin(),
                isJwtLogin: Boolean(isJwtTokenLogin()),
                hasExistingSession: isSessionDataLogin(),
                sessionExpired: liveParams.get(LOGIN_QUERY_PARAMS.SESSION_EXPIRED) === 'true',
            });

            const sessionExpired =
                liveParams.get(LOGIN_QUERY_PARAMS.SESSION_EXPIRED) === 'true' ||
                isSessionRejected();

            if (sessionExpired && !isExplicitAuthRequest()) {
                if (await tryRestoreCookieFromStoredToken()) {
                    return;
                }

                const sessionToken =
                    sessionService.getSessionToken() ??
                    webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
                const forcePurge = isSessionRejected();
                const probe = sessionToken
                    ? await probeSessionToken(sessionToken)
                    : ('dead' as const);

                // `session_expired` means shop SSR or a prior bounce already
                // rejected this session. Do not call handleSessionDataLogin here
                // — it navigates without the strict restore gate and loops when
                // the cookie cannot stick. Purge on proven-dead tokens and on
                // alive-but-unrestorable (cookie blocked); only `unknown`
                // keepalive failures keep storage so the player can retry.
                const shouldPurge =
                    forcePurge ||
                    !sessionToken ||
                    probe === 'dead' ||
                    probe === 'alive';

                if (shouldPurge) {
                    await purgeRejectedSession();
                    if (!cancelled) {
                        hideLoader();
                    }
                    reportLoginDeadEnd(LoginDeadEndReason.SESSION_EXPIRED_PURGE);
                    return;
                }

                if (!cancelled) {
                    hideLoader();
                }
                return;
            }

            if (autoLoginAttemptedRef.current) {
                return;
            }
            autoLoginAttemptedRef.current = true;

            const appleRedirectPending = webStorageUtil.get(ESessionStorageKeys.APPLE_REDIRECT);
            if (appleRedirectPending && !isAppleLogin()) {
                webStorageUtil.remove(ESessionStorageKeys.APPLE_REDIRECT);
                sendLoginCanceledWebhookEvents(AuthMethods.APPLE);
                hideLoader();
                reportLoginDeadEnd(LoginDeadEndReason.APPLE_REDIRECT_ABANDONED);
                return;
            }

            const redirectLoginToken = getRedirectFromPortalLoginToken()

            // The existing-session reuse path (`isSessionDataLogin`) is
            // intentionally excluded so sessions with no new auth request stay
            // untouched (backwards compatibility).
            //
            // Portal redirects always carry a fresh player context so we
            // unconditionally revoke the existing session. All other new-auth
            // methods still respect the feature flag.
            if (redirectLoginToken) {
                // The handoff token is single-use: the gateway deletes it from
                // Redis on the first exchange, yet it stays on the URL. Anything
                // that loads /login again — the game re-firing the deep link, a
                // back navigation, a remount — replays it, and that attempt 401s
                // a player who is already signed in and browsing, sending them
                // to /failed. 75% of handoff landings already hold a live
                // session. Mark and strip before the awaits below, exactly like
                // the OTP path (ACDEV-27624).
                //
                // Short-circuit only while there is a session to protect, since
                // that is what turns a replay into a logout. With no session the
                // exchange has nothing to lose and may still succeed: a first
                // attempt that died before the gateway consumed the token
                // (Android tearing down the WebView mid-request) leaves the
                // token valid, so dead-ending here would strand the player on a
                // token that still works.
                const replayedSession =
                    isPortalRedirectLoginAttempted(redirectLoginToken) &&
                    (sessionService.getSessionToken() ??
                        webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN));
                if (replayedSession) {
                    // Not a failure — the player keeps their session — but it is a
                    // handoff that produced no login, which is what a publisher
                    // sees as a player who arrived and never logged in.
                    reportLoginDeadEnd(LoginDeadEndReason.HANDOFF_REPLAY);
                    navigateToShopHard();
                    return;
                }
                markPortalRedirectLoginAttempted(redirectLoginToken);
                clearPortalRedirectLoginParamsFromUrl();
                clearSessionRejectedMark();
                await revokeExistingSession();
                showLoader();
                await handlePortalLoginRedirect(redirectLoginToken);
                return;
            }

            // Capture OTP credentials and arm the remount guard BEFORE any
            // await (revoke / login). Remounts reset autoLoginAttemptedRef
            // while OTP params are still on the URL; without this, a second
            // AuthController.login reuses consumed OTP credentials and the
            // player sees Whoops after a successful first login (ACDEV-27624).
            //
            // proofKey alone is enough to log in (store-frontend / playerCode).
            // The access token is optional and used for UTMs + campaign metadata.
            const otpCredentials = resolveOtpCredentials();
            const otpProofKey = otpCredentials?.proofKey;
            const otpToken = otpCredentials?.token;
            const isOtpCallback = Boolean(otpProofKey);
            const otpGuardKey = otpToken ?? otpProofKey;

            if (isOtpCallback && otpGuardKey && isOtpLoginAttempted(otpGuardKey)) {
                const existingSession =
                    sessionService.getSessionToken() ??
                    webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
                if (existingSession) {
                    navigateToShopHard();
                } else if (!cancelled) {
                    hideLoader();
                }
                return;
            }
            if (isOtpCallback && otpGuardKey) {
                markOtpLoginAttempted(otpGuardKey);
                clearOtpLoginParamsFromUrl();
                // Single-use: the stash exists only to survive the jump into the
                // game, so drop it before login runs. Keeping it would let a
                // later stray `?proofKey` replay a consumed token.
                clearPendingOtpAccessToken();
            }

            const isNewAuthRequest =
                isOtpCallback ||
                isAppleLogin() ||
                isFacebookLogin() ||
                Boolean(isJwtTokenLogin());
            if (isNewAuthRequest && featureFlagService.isEnabled(FeatureFlags.STORE_REVOKE_SESSION_ON_NEW_AUTH)) {
                await revokeExistingSession();
            }

            if (isOtpCallback) {
                clearSessionRejectedMark();
                showLoader();
                await handleOTPLogin(otpProofKey, otpToken);
                return;
            }

            if (isAppleLogin()) {
                clearSessionRejectedMark();
                webStorageUtil.remove(ESessionStorageKeys.APPLE_REDIRECT);
                showLoader();
                await handleAppleLogin();
                return;
            }

            if (isFacebookLogin()) {
                clearSessionRejectedMark();
                showLoader();
                await handleFacebookLogin();
                return;
            }

            if (isJwtTokenLogin()) {
                clearSessionRejectedMark();
                showLoader();
                // Use the persistent `session_id` cookie (set by middleware
                // and shared with the regular login path via
                // `sessionManager.getOrCreateSessionId()` in `prepareLoginData`).
                // The previous `sessionService.getSessionId()` was a UUID
                // generated per `sessionService.initialize()` call — different
                // from what the regular login path sends, and (until the RSC
                // singleton bug was fixed) read from a server singleton that
                // could leak across users.
                await handleJwtTokenLogin({
                    sessionId: sessionManager.getOrCreateSessionId(),
                    jwtToken: getUrlParam(URL_PARAMS.JWT_PARAM) ?? '',
                    publisherId: authBoot.storeTheme.publisherId
                });
                return;
            }

            if (isSessionDataLogin()) {
                await handleSessionDataLogin();
                return;
            }

            hideLoader();
        }

        void handleLoginByQueryParams();

        return () => {
            cancelled = true;
        };
    }, []);
}
